import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import {
  initMixpanel,
  isMixpanelInitialized,
  trackEvent,
  identifyUser,
  setUserProperties,
  setUserPropertiesOnce,
  registerSuperProperties,
  resetUser,
  trackPageView,
  incrementUserProperty,
  timeEvent,
} from '../services/mixpanel';

type EventProperties = Record<string, string | number | boolean | null | undefined>;

type UserProperties = {
  telegramId?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  walletAddress?: string;
  [key: string]: string | number | boolean | undefined;
};

/**
 * Initialize Mixpanel tracking.
 * Call this hook once at the app root level.
 * @param options - Configuration options
 * @param enabled - Whether to initialize Mixpanel (default: true, use for deferred init)
 */
export function useMixpanelInit(options: { trackPageViews?: boolean } = {}, enabled: boolean = true) {
  const { trackPageViews = true } = options;
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);
  const fallbackToken = 'd716e8548ef09c098e4a1d17f2b7a26e';
  const isProd = import.meta.env.MODE === 'production';

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    const envToken = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
    const token = envToken ?? (isProd ? fallbackToken : undefined);

    if (!token) {
      console.warn('[useMixpanelInit] VITE_MIXPANEL_TOKEN not set (fallback only active in production)');
      return;
    }

    if (!isMixpanelInitialized()) {
      initMixpanel({
        token,
        debug: import.meta.env.DEV,
        trackPageview: true,
      });
    }
  }, [enabled]);

  // Track page views on route change
  useEffect(() => {
    if (!trackPageViews || !isMixpanelInitialized()) {
      return;
    }

    const currentPath = location.pathname;

    // Avoid tracking the same page twice
    if (prevPathRef.current !== currentPath) {
      trackPageView(currentPath, {
        search: location.search,
        hash: location.hash,
      });
      prevPathRef.current = currentPath;
    }
  }, [location, trackPageViews]);
}

/**
 * Hook for tracking analytics events.
 * Returns memoized functions for tracking various analytics actions.
 */
export function useMixpanel() {
  const track = useCallback((eventName: string, properties?: EventProperties) => {
    trackEvent(eventName, properties);
  }, []);

  const identify = useCallback((userId: string) => {
    identifyUser(userId);
  }, []);

  const setProperties = useCallback((properties: UserProperties) => {
    setUserProperties(properties);
  }, []);

  const setPropertiesOnce = useCallback((properties: UserProperties) => {
    setUserPropertiesOnce(properties);
  }, []);

  const setSuperProperties = useCallback((properties: EventProperties) => {
    registerSuperProperties(properties);
  }, []);

  const reset = useCallback(() => {
    resetUser();
  }, []);

  const pageView = useCallback((pageName: string, properties?: EventProperties) => {
    trackPageView(pageName, properties);
  }, []);

  const increment = useCallback((property: string, value: number = 1) => {
    incrementUserProperty(property, value);
  }, []);

  const startTiming = useCallback((eventName: string) => {
    timeEvent(eventName);
  }, []);

  return {
    track,
    identify,
    setProperties,
    setPropertiesOnce,
    setSuperProperties,
    reset,
    pageView,
    increment,
    startTiming,
    isInitialized: isMixpanelInitialized,
  };
}

/**
 * Hook for tracking a specific event on component mount
 */
export function useMixpanelTrackOnMount(eventName: string, properties?: EventProperties) {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!hasTracked.current && isMixpanelInitialized()) {
      trackEvent(eventName, properties);
      hasTracked.current = true;
    }
  }, [eventName, properties]);
}

/**
 * Hook for identifying user with Telegram data
 * @param enabled - Whether to identify user (default: true, use for deferred init)
 */
export function useMixpanelIdentifyTelegramUser(enabled: boolean = true) {
  const hasIdentified = useRef(false);

  useEffect(() => {
    if (!enabled || hasIdentified.current || !isMixpanelInitialized()) {
      return;
    }

    const tg = window.Telegram?.WebApp;
    const user = tg?.initDataUnsafe?.user;

    if (user?.id) {
      identifyUser(String(user.id));

      setUserPropertiesOnce({
        telegramId: String(user.id),
        username: user.username,
        firstName: user.first_name,
        lastName: user.last_name,
        $name: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username,
      });

      registerSuperProperties({
        platform: 'telegram',
        telegramPlatform: tg?.platform,
        telegramVersion: tg?.version,
      });

      hasIdentified.current = true;
    }
  }, [enabled]);
}

